import { ChangeDetectionStrategy, Component, ElementRef, Input, inject } from '@angular/core';
import { TrackingEvents } from '../_shared/data/tracking-events.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import { ITrackingParams } from '../_shared/models/tracking/tracking-params.model';
import { GtmService } from '../_shared/services/gtm.service';
import { LinkService } from '../_shared/services/link.service';
import { SnowplowService } from '../_shared/services/snowplow.service';
import { ButtonSize } from '../_shared/types/button/button-size.type';
import { ButtonType } from '../_shared/types/button/button.type';
import { IconPosition } from '../_shared/types/icon-position.type';

import { IconComponent } from '../icon/icon.component';
import { IconName } from '../_shared/models/icon.model';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'button[siriusButton], a[siriusButton]',
    exportAs: 'siriusButton',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    host: {
        'class': 'sir-text-style-button-m-default',
        '[class.is-primary]': "buttonType === 'primary'",
        '[class.is-secondary]': "buttonType === 'secondary'",
        '[class.is-linkstyle]': "buttonType === 'linkstyle'",
        '[class.is-small]': "size === 'small'",
        '[class.is-medium]': "size === 'medium'",
        '[class.is-large]': "size === 'large'",
        '[class.is-icon-only]': 'isIconOnly',
        '[class.has-icon-start]': 'hasIconStart',
        '[attr.disabled]': 'nativeDisabled',
        '[attr.aria-disabled]': 'ariaDisabled',
        '(click)': 'onClick($event)',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent],
})
export class ButtonComponent {
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _elRef = inject<ElementRef<HTMLAnchorElement>>(ElementRef);
    private _linkService = inject(LinkService);
    private _linkDirective = inject(LinkDirective, { optional: true });

    @Input() buttonType: ButtonType = 'primary';
    @Input() buttonText = '';
    @Input() disabled = false;
    @Input() iconPos: IconPosition = 'right';
    @Input() iconName?: IconName;
    @Input() size: ButtonSize = 'medium';
    @Input() tracking?: ITrackingParams;

    protected get hasIconStart(): boolean {
        return this.iconName !== undefined && this.iconPos === 'left' && this.buttonText.length > 0;
    }

    protected get isIconOnly(): boolean {
        return this.iconName !== undefined && this.buttonText.length <= 0;
    }

    /** Only valid on a `button` host; an anchor is held back by `onClick` instead. */
    protected get nativeDisabled(): true | null {
        return this.disabled && this.isNativeButton ? true : null;
    }

    protected get ariaDisabled(): 'true' | null {
        return this.disabled ? 'true' : null;
    }

    onClick(event: Event): void {
        if (this.disabled) {
            // The component is always the first directive on its host, so stopping
            // propagation here runs before `LinkDirective` can navigate.
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        this.addTracking();
    }

    private get isNativeButton(): boolean {
        return this._elRef.nativeElement.tagName === 'BUTTON';
    }

    private addTracking() {
        const tracking = this.tracking ?? this._linkDirective?.siriusLink()?.tracking;

        if (!tracking) return;

        const el = this._elRef.nativeElement;
        const data = this._linkService.getTrackingData(el);

        const cfg = {
            ...tracking?.parameters,
            link_url: data.linkUrl,
            link_classes: data.linkClasses,
            link_text: data.linkText,
            link_id: data.linkId,
            outbound: data.outbound,
        };

        this._gtmService.track(TrackingEvents.gtm['buttonClick']([cfg]));

        const cfg2 = {
            element_classes: [...data.linkClasses.split(' ')],
            element_content: data.linkText.length ? data.linkText : 'icon',
            identifier: tracking?.parameters['identifier'] ? tracking?.parameters['identifier'] : 'null',
            target_url: data.linkUrl || null,
            context: [],
        };

        this._snowplowService.track('buttonClick', cfg2);
    }
}
